/*
 * PERF INVARIANT — DO NOT reintroduce sync fs APIs (writeFileSync /
 * appendFileSync) in this file's write paths. `ReportGenerator` runs on
 * the Electron main event loop during agent execution, and a single
 * progress tick appends a multi-MB ExecutionDump payload. Sync I/O here
 * blocked the loop for 20+ seconds per run, freezing IPC, scrcpy and
 * every renderer round-trip. Always use `fs/promises`. See commit
 * 6a25e05c and `report-generator-async-contract.test.ts`.
 */
import { existsSync, mkdirSync, readdirSync } from 'node:fs';
import {
  appendFile as appendFileAsync,
  writeFile as writeFileAsync,
} from 'node:fs/promises';
import { dirname, join } from 'node:path';
import { getMidsceneRunSubDir } from '@midscene/shared/common';
import {
  MIDSCENE_REPORT_QUIET,
  globalConfigManager,
} from '@midscene/shared/env';
import { ifInBrowser, logMsg, uuid } from '@midscene/shared/utils';
import {
  DATA_SCREENSHOT_MODE_ATTR,
  generateAgentReportComment,
  generateDumpScriptTag,
  generateImageScriptTag,
  getBaseUrlFixScript,
} from './dump/html-utils';
import { ScreenshotStore } from './dump/screenshot-store';
import {
  type ExecutionDump,
  ReportActionDump,
  type ReportAttributes,
  type ReportMeta,
  type ScreenshotMode,
} from './types';
import { getReportTpl } from './utils';

export interface IReportGenerator {
  /**
   * Write or update a single execution.
   * Each call appends a new dump script tag. The frontend deduplicates
   * executions with the same id/name, keeping only the last one.
   *
   * @param execution  Current execution's full data
   * @param reportMeta  Report-level metadata (groupName, sdkVersion, etc.)
   */
  onExecutionUpdate(
    execution: ExecutionDump,
    reportMeta: ReportMeta,
    attributes?: ReportAttributes,
  ): void;

  /**
   * @deprecated Use onExecutionUpdate instead. Kept for backward compatibility.
   */
  onDumpUpdate?(dump: ReportActionDump): void;

  /**
   * Wait for all queued write operations to complete.
   */
  flush(): Promise<void>;

  /**
   * Finalize the report. Calls flush() internally.
   */
  finalize(): Promise<string | undefined>;

  getReportPath(): string | undefined;
}

export const nullReportGenerator: IReportGenerator = {
  onExecutionUpdate: () => {},
  flush: async () => {},
  finalize: async () => undefined,
  getReportPath: () => undefined,
};

export function assertReportGenerationOptions(opts: {
  generateReport?: boolean;
  persistExecutionDump?: boolean;
}): void {
  if (opts.generateReport === false && opts.persistExecutionDump === true) {
    throw new Error(
      'persistExecutionDump cannot be true when generateReport is false',
    );
  }
}

export class ReportGenerator implements IReportGenerator {
  private reportPath: string;
  private screenshotMode: ScreenshotMode;
  private shouldPersistExecutionDump: boolean;
  private autoPrint: boolean;
  private firstWriteDone = false;
  private executionLogIndex = 0;
  private executionLogFileIndexByExecutionKey = new Map<string, number>();

  // Unique identifier for this report stream — used as data-group-id
  private readonly reportStreamId: string;

  // Tracks screenshots already written to disk (by id) to avoid duplicates
  private screenshotStore: ScreenshotStore;
  private initialized = false;

  // Tracks the last execution + groupMeta for re-writing on finalize
  private lastExecution?: ExecutionDump;
  private lastReportMeta?: ReportMeta;
  private executionsByKey = new Map<string, ExecutionDump>();
  private executionCommentKeyByObject = new WeakMap<ExecutionDump, string>();
  private executionCommentKeyIndex = 0;
  private reportAttributes: Record<string, string> = {};
  private agentCommentWritten = false;

  // write queue for serial execution
  private writeQueue: Promise<void> = Promise.resolve();
  private destroyed = false;

  constructor(options: {
    reportPath: string;
    screenshotMode: ScreenshotMode;
    persistExecutionDump?: boolean;
    autoPrint?: boolean;
    reuseExistingReport?: boolean;
  }) {
    this.reportPath = options.reportPath;
    this.screenshotMode = options.screenshotMode;
    this.shouldPersistExecutionDump = options.persistExecutionDump ?? false;
    this.autoPrint = options.autoPrint ?? true;
    this.reportStreamId = uuid();
    this.screenshotStore = new ScreenshotStore({
      mode: this.screenshotMode === 'inline' ? 'inline' : 'directory',
      reportPath: this.reportPath,
      screenshotsDir: join(dirname(this.reportPath), 'screenshots'),
      writeInlineImage: async (id, base64) => {
        await appendFileAsync(
          this.reportPath,
          `\n${generateImageScriptTag(id, base64)}`,
        );
      },
      alsoWriteFileCopy: this.shouldPersistExecutionDump,
    });
    if (options.reuseExistingReport) {
      this.hydrateStateFromExistingReport();
    }
  }

  static create(
    reportFileName: string,
    opts: {
      generateReport?: boolean;
      persistExecutionDump?: boolean;
      outputFormat?: 'single-html' | 'html-and-external-assets';
      autoPrintReportMsg?: boolean;
      reuseExistingReport?: boolean;
    },
  ): IReportGenerator {
    assertReportGenerationOptions(opts);
    validateReportFileName(reportFileName);
    if (opts.generateReport === false) return nullReportGenerator;

    // In browser environment, file system is not available
    if (ifInBrowser) return nullReportGenerator;

    const reportRootDir = getMidsceneRunSubDir('report');
    const outputDir = join(reportRootDir, reportFileName);
    const reportPath =
      opts.outputFormat === 'html-and-external-assets'
        ? join(outputDir, 'index.html')
        : join(reportRootDir, ensureHtmlFileName(reportFileName));
    return new ReportGenerator({
      reportPath,
      screenshotMode:
        opts.outputFormat === 'html-and-external-assets'
          ? 'directory'
          : 'inline',
      persistExecutionDump: opts.persistExecutionDump,
      autoPrint: opts.autoPrintReportMsg,
      reuseExistingReport: opts.reuseExistingReport,
    });
  }

  onExecutionUpdate(
    execution: ExecutionDump,
    reportMeta: ReportMeta,
    attributes?: ReportAttributes,
  ): void {
    this.lastExecution = execution;
    this.lastReportMeta = reportMeta;
    this.executionsByKey.set(this.getExecutionCommentKey(execution), execution);
    this.mergeReportAttributes(attributes);
    this.writeQueue = this.writeQueue.then(async () => {
      if (this.destroyed) return;
      await this.doWriteExecution(execution, reportMeta);
    });
  }

  async flush(): Promise<void> {
    await this.writeQueue;
  }

  async finalize(): Promise<string | undefined> {
    // Re-write the last execution to capture any final state changes
    if (this.lastExecution && this.lastReportMeta) {
      this.onExecutionUpdate(this.lastExecution, this.lastReportMeta);
    }
    await this.flush();
    this.destroyed = true;

    if (!this.initialized) {
      // No executions were ever written — no file exists
      return undefined;
    }

    await this.appendAgentReportComment();
    return this.reportPath;
  }

  getReportPath(): string | undefined {
    return this.reportPath;
  }

  private printReportPath(): void {
    if (!this.autoPrint || !this.reportPath) return;
    if (globalConfigManager.getEnvConfigInBoolean(MIDSCENE_REPORT_QUIET))
      return;

    if (this.screenshotMode === 'directory') {
      logMsg(
        `Midscene - report file updated: npx serve ${dirname(this.reportPath)}`,
      );
    } else {
      logMsg(`Midscene - report file updated: ${this.reportPath}`);
    }
  }

  private async doWriteExecution(
    execution: ExecutionDump,
    reportMeta: ReportMeta,
  ): Promise<void> {
    const singleDump = this.wrapAsReportDump(execution, reportMeta);

    if (this.screenshotMode === 'inline') {
      await this.writeInlineExecution(execution, singleDump);
    } else {
      await this.writeDirectoryExecution(execution, singleDump);
    }

    if (this.shouldPersistExecutionDump) {
      await this.persistExecutionDumpToFile(execution, singleDump);
    }

    if (!this.firstWriteDone) {
      this.firstWriteDone = true;
      this.printReportPath();
    }
  }

  private mergeReportAttributes(attributes?: ReportAttributes): void {
    if (!attributes) {
      return;
    }

    for (const [key, value] of Object.entries(attributes)) {
      if (value === undefined || value === null) {
        continue;
      }
      this.reportAttributes[key] = String(value);
    }
  }

  private hydrateStateFromExistingReport(): void {
    if (!existsSync(this.reportPath)) {
      return;
    }

    // Reuse existing report file and append new updates instead of rewriting.
    this.initialized = true;

    if (!this.shouldPersistExecutionDump) {
      return;
    }

    const reportDir = dirname(this.reportPath);
    const existingExecutionIndices = readdirSync(reportDir)
      .map((name) => /^(\d+)\.execution\.json$/.exec(name)?.[1])
      .filter((index): index is string => Boolean(index))
      .map((index) => Number.parseInt(index, 10))
      .filter((index) => Number.isFinite(index));

    if (existingExecutionIndices.length > 0) {
      this.executionLogIndex = Math.max(...existingExecutionIndices);
    }
  }

  private getDumpScriptAttributes(): Record<string, string> {
    return {
      'data-group-id': this.reportStreamId,
      // Self-describe how this report file stores screenshots so consumers
      // (merge/delete) never have to guess the mode from the filesystem.
      [DATA_SCREENSHOT_MODE_ATTR]: this.screenshotMode,
      ...this.reportAttributes,
    };
  }

  /**
   * Wrap an ExecutionDump + ReportMeta into a single-execution ReportActionDump.
   */
  private wrapAsReportDump(
    execution: ExecutionDump,
    reportMeta: ReportMeta,
  ): ReportActionDump {
    return new ReportActionDump({
      sdkVersion: reportMeta.sdkVersion,
      groupName: reportMeta.groupName,
      groupDescription: reportMeta.groupDescription,
      modelBriefs: reportMeta.modelBriefs,
      deviceType: reportMeta.deviceType,
      executions: [execution],
    });
  }

  /**
   * Append-only inline mode: write new screenshots and a dump tag on every call.
   * The frontend deduplicates executions with the same id/name (keeps last).
   * Duplicate dump JSON is acceptable; only screenshots are deduplicated.
   *
   * All writes go through `fs/promises` so they run on libuv's thread pool
   * rather than blocking the Node event loop. A long agent run previously
   * appended multi-MB dumps (screenshots + serialized tasks) per progress
   * tick on the main thread, starving IPC and UI for >10s per stall.
   */
  private async writeInlineExecution(
    execution: ExecutionDump,
    singleDump: ReportActionDump,
  ): Promise<void> {
    const dir = dirname(this.reportPath);
    if (!existsSync(dir)) {
      mkdirSync(dir, { recursive: true });
    }

    // Initialize: write HTML template once
    if (!this.initialized) {
      await writeFileAsync(this.reportPath, getReportTpl());
      this.initialized = true;
    }

    // Append new screenshots (skip already-written ones)
    for (const screenshot of execution.collectScreenshots()) {
      await this.screenshotStore.persist(screenshot);
    }

    // Append dump tag (always — frontend keeps only last per execution id)
    const serialized = singleDump.serialize();
    await appendFileAsync(
      this.reportPath,
      `\n${generateDumpScriptTag(serialized, this.getDumpScriptAttributes())}`,
    );
  }

  private async writeDirectoryExecution(
    execution: ExecutionDump,
    singleDump: ReportActionDump,
  ): Promise<void> {
    const dir = dirname(this.reportPath);
    if (!existsSync(dir)) {
      mkdirSync(dir, { recursive: true });
    }

    for (const screenshot of execution.collectScreenshots()) {
      await this.screenshotStore.persist(screenshot);
    }

    // 2. Append dump tag (always — frontend keeps only last per execution id)
    const serialized = singleDump.serialize();

    if (!this.initialized) {
      await writeFileAsync(
        this.reportPath,
        `${getReportTpl()}${getBaseUrlFixScript()}`,
      );
      this.initialized = true;
    }

    await appendFileAsync(
      this.reportPath,
      `\n${generateDumpScriptTag(serialized, this.getDumpScriptAttributes())}`,
    );
  }

  private async appendAgentReportComment(): Promise<void> {
    if (
      this.agentCommentWritten ||
      !this.lastReportMeta ||
      this.executionsByKey.size === 0
    ) {
      return;
    }

    const reportDump = new ReportActionDump({
      sdkVersion: this.lastReportMeta.sdkVersion,
      groupName: this.lastReportMeta.groupName,
      groupDescription: this.lastReportMeta.groupDescription,
      modelBriefs: this.lastReportMeta.modelBriefs,
      deviceType: this.lastReportMeta.deviceType,
      executions: Array.from(this.executionsByKey.values()),
    });
    await appendFileAsync(
      this.reportPath,
      `\n${generateAgentReportComment(reportDump)}`,
    );
    this.agentCommentWritten = true;
  }

  private getExecutionLogKey(execution: ExecutionDump): string {
    if (!execution.id) {
      throw new Error(
        'ReportGenerator: execution.id is required for persisting execution dumps',
      );
    }
    return `id:${execution.id}`;
  }

  private getExecutionCommentKey(execution: ExecutionDump): string {
    if (execution.id) {
      return `id:${execution.id}`;
    }

    const existingKey = this.executionCommentKeyByObject.get(execution);
    if (existingKey) {
      return existingKey;
    }

    const key = `no-id:${this.executionCommentKeyIndex++}`;
    this.executionCommentKeyByObject.set(execution, key);
    return key;
  }

  private async persistExecutionDumpToFile(
    execution: ExecutionDump,
    singleDump: ReportActionDump,
  ): Promise<void> {
    const dir = dirname(this.reportPath);
    if (!existsSync(dir)) {
      mkdirSync(dir, { recursive: true });
    }

    const executionLogKey = this.getExecutionLogKey(execution);
    let fileIndex =
      this.executionLogFileIndexByExecutionKey.get(executionLogKey);
    if (!fileIndex) {
      this.executionLogIndex += 1;
      fileIndex = this.executionLogIndex;
      this.executionLogFileIndexByExecutionKey.set(executionLogKey, fileIndex);
    }

    const fileName = `${fileIndex}.execution.json`;
    const filePath = join(dirname(this.reportPath), fileName);
    await writeFileAsync(filePath, singleDump.serialize(2), 'utf-8');
  }
}

function ensureHtmlFileName(reportFileName: string): string {
  return reportFileName.endsWith('.html')
    ? reportFileName
    : `${reportFileName}.html`;
}

function validateReportFileName(reportFileName: string): void {
  if (!reportFileName?.trim()) {
    throw new Error('reportFileName must be a non-empty string');
  }

  if (/[\\/]/.test(reportFileName)) {
    throw new Error(
      'reportFileName must not contain path separators (`/` or `\\\\`)',
    );
  }

  if (/[:*?"<>|]/.test(reportFileName)) {
    throw new Error(
      'reportFileName contains illegal filename characters: : * ? " < > |',
    );
  }
}
